import React, { useState, useEffect } from 'react';
import { Loader2, Lock, Eye, AlertCircle } from 'lucide-react';
import { getShare, incrementShareView } from '../services/shareService.js';
import ModernTemplate from './templates/ModernTemplate';
import ClassicTemplate from './templates/ClassicTemplate';
import MinimalistTemplate from './templates/MinimalistTemplate';
import CreativeTemplate from './templates/CreativeTemplate';
import ProfessionalTemplate from './templates/ProfessionalTemplate';

const TEMPLATES = {
    modern: ModernTemplate,
    classic: ClassicTemplate,
    minimalist: MinimalistTemplate,
    creative: CreativeTemplate,
    professional: ProfessionalTemplate
};

const SharedResumeView = ({ shareId }) => {
    const [state, setState] = useState({ status: 'loading' });
    const [password, setPassword] = useState('');

    const load = async (pw) => {
        setState({ status: 'loading' });
        try {
            const share = await getShare(shareId, pw);
            incrementShareView(shareId); // fire-and-forget
            setState({ status: 'ready', share });
        } catch (err) {
            if (err.code === 'password-required') setState({ status: 'password' });
            else if (err.code === 'password-incorrect') setState({ status: 'password', error: 'Incorrect password.' });
            else setState({ status: 'error', error: err.message });
        }
    };

    useEffect(() => { load(); }, [shareId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (state.status === 'loading') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-100">
                <Loader2 className="animate-spin text-blue-600" size={32} />
            </div>
        );
    }

    if (state.status === 'password') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-sm w-full">
                    <div className="flex items-center gap-3 mb-4">
                        <Lock className="text-blue-600" size={24} />
                        <h2 className="font-bold text-slate-800">Password Protected</h2>
                    </div>
                    <p className="text-sm text-slate-500 mb-4">Enter the password to view this resume.</p>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && load(password)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        autoFocus
                    />
                    {state.error && (
                        <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                            <AlertCircle size={12} /> {state.error}
                        </p>
                    )}
                    <button
                        onClick={() => load(password)}
                        className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded-lg transition-colors"
                    >
                        Unlock
                    </button>
                </div>
            </div>
        );
    }

    if (state.status === 'error') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-sm text-center">
                    <AlertCircle className="text-red-500 mx-auto mb-3" size={32} />
                    <h2 className="font-bold text-slate-800 mb-2">Cannot Load Resume</h2>
                    <p className="text-sm text-slate-500">{state.error}</p>
                </div>
            </div>
        );
    }

    const { share } = state;
    const Template = TEMPLATES[share.template] || ModernTemplate;

    return (
        <div className="min-h-screen bg-slate-100">
            <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center justify-between text-xs text-slate-500">
                <span>Shared via Resume Architect</span>
                <span className="flex items-center gap-1">
                    <Eye size={12} /> {share.viewCount + 1} view{share.viewCount + 1 === 1 ? '' : 's'}
                </span>
            </div>
            <div className="max-w-4xl mx-auto p-4 md:p-8">
                <Template resumeData={share.resumeData} />
            </div>
        </div>
    );
};

export default SharedResumeView;
